// CalibrationLogSection.js
// Shared, context-agnostic calibration log section (card list + add/edit modal + delete confirm).
// Driven entirely by injected props so it works in both the Sampling Event form and the standalone
// Calibration Log Book. The calibration row behavior (auto-fill, pass/fail, tempState commit) lives
// here as the single source of truth.

import React from "react";
// Components (reused from the sampling event feature)
import { ConfirmationModal } from "../../samplingEventForm/common/ConfirmationModal";
import { CalibrationLogModal } from "./components/CalibrationLogModal";
import styles from "./CalibrationLog.module.css";
// Util functions
import { formatDateForDisplay } from "../../samplingEventForm/samplingEventUtils";
// Store
import { useAppStore } from "../../../store/appStore";

// Normalize pass/fail display text
const getPassFailDisplay = (value) => {
  if (!value) return "";
  const upperValue = value.toUpperCase();
  if (upperValue === "Y" || upperValue === "YES" || upperValue === "PASS") {
    return "Pass";
  } else if (upperValue === "N" || upperValue === "NO" || upperValue === "FAIL") {
    return "Fail";
  }
  return value;
};

// CalibrationLogCard component (mobile & tablet)
const CalibrationLogCard = ({ log, handleEditLog, handleRemoveLog }) => {
  return (
    <div key={log.id} className={styles.logCard}>
      <div className={styles.logCardHeader}>
        <div className={styles.logDateTime}>
          {log.calibrationDate ? formatDateForDisplay(log.calibrationDate) : "No Date"}{" "}
          {log.calibrationTime ? log.calibrationTime : ""}
        </div>
      </div>
      <div className={styles.logCardContent}>
        <div className={styles.logField}>
          <div className={styles.logLabel}>Instrument</div>
          <div className={styles.logValue}>{log.instrument || "—"}</div>
        </div>
        <div className={styles.logField}>
          <div className={styles.logLabel}>Field Param</div>
          <div className={styles.logValue}>{log.fieldParameter || "—"}</div>
        </div>
        <div className={styles.logField}>
          <div className={styles.logLabel}>Standard Used</div>
          <div className={styles.logValue}>{log.standardUsed || "—"}</div>
        </div>
        <div className={styles.logField}>
          <div className={styles.logLabel}>Verification</div>
          <div className={styles.logValue}>{log.verification || "—"}</div>
        </div>
        <div className={styles.logField}>
          <div className={styles.logLabel}>Pass/Fail</div>
          <div className={styles.logValue}>{getPassFailDisplay(log.passFail) || "—"}</div>
        </div>
        <div className={styles.logField}>
          <div className={styles.logLabel}>Name</div>
          <div className={styles.logValue}>{log.employeeName || "—"}</div>
        </div>
      </div>
      <div className={styles.logActions}>
        <button className={styles.editButton} onClick={() => handleEditLog(log.id)}>
          Edit
        </button>
        <button className={styles.deleteButton} onClick={() => handleRemoveLog(log.id)}>
          Delete
        </button>
      </div>
    </div>
  );
};

/**
 * Shared Calibration Log Section.
 *
 * @param {function} getSortedLogs - returns the sorted array of calibration log instances
 * @param {object} ui - calibration log UI state {isModalOpen, currentLogId, isAddMode, modalHeaderString, isDeleteConfirmOpen, logToDelete}
 * @param {object} clActions - calibration log section actions (openModal, setCurrentLogId, closeModal, openDeleteConfirm, closeDeleteConfirm, commitTempCalibrationLogs, removeCalibrationLog)
 * @param {function} emitPatch - socket emit function
 * @param {object} user - current user {employee_id, name}
 * @param {array} meters - equipment meters with nested calibration_parameters
 * @param {function} getExistingLogById - (id) => log instance, for populating the edit modal
 * @param {object} parentData - the parent model holding the logs (sampling event or book)
 * @param {string} sectionTitle - heading text
 */
export const CalibrationLogSection = ({
  getSortedLogs,
  ui = {},
  clActions = {},
  emitPatch,
  user,
  meters = [],
  getExistingLogById,
  parentData,
  sectionTitle = "Water Quality Meter Calibration Log",
}) => {
  const showNotification = useAppStore(
    (s) => s.notificationActions.showNotification
  );

  const sortedCalibrationLogs = getSortedLogs ? getSortedLogs() : [];

  // Open modal in "add" mode
  const handleAddLog = () => {
    clActions.openModal("Add Calibration Log", true);
  };

  // Open modal in "edit" mode
  const handleEditLog = (logId) => {
    clActions.setCurrentLogId(logId);
    clActions.openModal("Edit Calibration Log", false);
  };

  // Commit tempState to data + server
  const handleSaveLogs = async (tempState) => {
    const result = await clActions.commitTempCalibrationLogs(
      tempState,
      parentData,
      emitPatch
    );

    clActions.closeModal();

    if (result.ok) {
      if (result.warnings && result.warnings.length > 0) {
        showNotification({
          type: "warning",
          title: "Logs Saved with Warnings",
          message: "Logs saved locally. Some server sync issues may have occurred.",
        });
      } else {
        showNotification({
          type: "success",
          title: "Logs Saved",
          message: "The logs have been saved successfully",
        });
      }
    } else {
      showNotification({
        type: "error",
        title: "Error Saving Logs",
        message: result.error || "An error occurred while saving logs",
      });
    }
  };

  const handleOpenDeleteConfirm = (logId) => {
    clActions.openDeleteConfirm(logId);
  };

  const handleCloseDeleteConfirm = () => {
    clActions.closeDeleteConfirm();
  };

  const handleRemoveLog = (logId) => {
    clActions.removeCalibrationLog(logId, emitPatch);
  };

  return (
    <div className={styles.calibrationLogSection}>
      <div className={styles.sectionHeader}>
        <h4>{sectionTitle}</h4>
        <button className={styles.addLogButton} onClick={handleAddLog}>
          Add Log Session
        </button>
      </div>

      {sortedCalibrationLogs?.length > 0 ? (
        <>
          {/* Card View (mobile & tablet) */}
          <div className={styles.logsCardContainer}>
            {sortedCalibrationLogs.map((log) => (
              <CalibrationLogCard
                key={log.id}
                log={log}
                handleEditLog={handleEditLog}
                handleRemoveLog={handleOpenDeleteConfirm}
              />
            ))}
          </div>

          {/* Table View (large screens) */}
          <div className={styles.logsTableWrapper}>
            <table className={styles.logsTable}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Time</th>
                  <th>Instrument</th>
                  <th>Field Param</th>
                  <th>Standard</th>
                  <th>Verification</th>
                  <th>Units</th>
                  <th>Pass/Fail</th>
                  <th>Name</th>
                  <th className={styles.actionsColumn}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedCalibrationLogs.map((log) => (
                  <tr key={`row-${log.id}`}>
                    <td>
                      {log.calibrationDate
                        ? formatDateForDisplay(log.calibrationDate)
                        : "—"}
                    </td>
                    <td>{log.calibrationTime || "—"}</td>
                    <td>{log.instrument || "—"}</td>
                    <td>{log.fieldParameter || "—"}</td>
                    <td>{log.standardUsed || "—"}</td>
                    <td>{log.verification || "—"}</td>
                    <td>{log.units || "—"}</td>
                    <td>{getPassFailDisplay(log.passFail) || "—"}</td>
                    <td>{log.employeeName || "—"}</td>
                    <td className={styles.actionsColumn}>
                      <button
                        className={styles.editButton}
                        onClick={() => handleEditLog(log.id)}
                      >
                        Edit
                      </button>
                      <button
                        className={styles.deleteButton}
                        onClick={() => handleOpenDeleteConfirm(log.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className={styles.noLogs}>
          No calibration logs found. Click "Add Log Session" to add a new
          calibration log.
        </div>
      )}

      {/* Log Edit/Add Modal */}
      {ui.isModalOpen && (
        <CalibrationLogModal
          onClose={() => clActions.closeModal()}
          onSave={handleSaveLogs}
          editLogId={ui.currentLogId}
          isAddMode={ui.isAddMode}
          headerString={ui.modalHeaderString}
          user={user}
          meters={meters || []}
          getExistingLogById={getExistingLogById}
        />
      )}

      {/* Confirmation Modal for Deletion */}
      {ui.isDeleteConfirmOpen && (
        <ConfirmationModal
          title="Delete Calibration Log"
          message="Are you sure you want to delete this calibration log? This action cannot be undone."
          onCancel={handleCloseDeleteConfirm}
          onConfirm={() => {
            const log = ui.logToDelete;
            if (!log) return;
            handleRemoveLog(log);
            clActions.closeDeleteConfirm();
          }}
          styles={styles}
          confirmButtonClass="confirmDelete"
        />
      )}
    </div>
  );
};
